import React from 'react';
import { useTranslation } from 'react-i18next';
import { localizePath } from '../../seo/site';
import { motion } from 'framer-motion';

const Destinations = () => {
  const { t, i18n } = useTranslation();

  const europeanCountries = [
    'germany', 'france', 'netherlands', 'belgium', 'unitedKingdom', 
    'spain', 'italy', 'czechRepublic', 'slovakia', 'austria', 
    'hungary', 'romania', 'bulgaria', 'denmark', 'sweden', 'norway',
    'finland', 'estonia', 'latvia', 'lithuania', 'poland', 'slovenia',
    'croatia', 'bosniaHerzegovina', 'serbia', 'montenegro', 'northMacedonia',
    'albania', 'greece', 'switzerland', 'luxembourg', 'portugal', 'ireland',
    'iceland', 'malta', 'cyprus', 'moldova', 'ukraine', 'belarus'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white section-padding">
        <div className="container-custom">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('destinations.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200">
              {t('destinations.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Road Transport Coverage */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('destinations.roadTransport.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('destinations.roadTransport.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Map */}
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl overflow-hidden h-96 relative">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center text-center text-white">
                    <div>
                      <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      <h3 className="text-2xl font-bold mb-2">{t('destinations.mapTitle')}</h3>
                      <p className="text-sm opacity-90">{t('destinations.mapSubtitle')}</p>
                    </div>
                  </div>
                </div>
                
                {/* Route indicators */}
                <div className="absolute -top-4 -right-4 bg-orange-accent text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {t('destinations.countriesCount')}
                </div>
                <div className="absolute -bottom-4 -left-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {t('destinations.dailyRoutes')}
                </div>
              </div>

              {/* Countries List */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('destinations.coveredCountries')}
                </h3>
                <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {europeanCountries.map((country, index) => (
                    <motion.div
                      key={country}
                      initial={false}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      viewport={{ once: true }}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-orange-50 transition-colors"
                    >
                      <div className="w-3 h-3 bg-orange-accent rounded-full"></div>
                      <span className="text-gray-800 font-medium text-sm">{t(`countries.${country}`)}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('destinations.keyFeatures')}</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{t('destinations.features.regularRoutes')}</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{t('destinations.features.deliveryOptions')}</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{t('destinations.features.insurance')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sea Freight Destinations */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('destinations.seaFreight.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('destinations.seaFreight.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* China */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gradient-to-br from-red-600 to-yellow-500">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-2">🇨🇳</div>
                      <h3 className="text-2xl font-bold">{t('destinations.seaFreight.countries.china')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.majorPorts')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>• Shanghai</div>
                        <div>• Shenzhen</div>
                        <div>• Ningbo</div>
                        <div>• Qingdao</div>
                        <div>• Tianjin</div>
                        <div>• Guangzhou</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.transitTime')}</h4>
                      <p className="text-sm text-gray-600">{t('destinations.transitTimes.chinaToEurope')}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.services')}</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• {t('destinations.seaFreightServices.fclLcl')}</li>
                        <li>• {t('destinations.seaFreightServices.factoryPickup')}</li>
                        <li>• {t('destinations.seaFreightServices.consolidation')}</li>
                        <li>• {t('destinations.seaFreightServices.qualityInspection')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* USA */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gradient-to-br from-blue-700 to-red-600">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-2">🇺🇸</div>
                      <h3 className="text-2xl font-bold">{t('destinations.seaFreight.countries.usa')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.majorPorts')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>• Los Angeles</div>
                        <div>• Long Beach</div>
                        <div>• New York</div>
                        <div>• Savannah</div>
                        <div>• Seattle</div>
                        <div>• Charleston</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.transitTime')}</h4>
                      <p className="text-sm text-gray-600">{t('destinations.transitTimes.usaToEurope')}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.services')}</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• {t('destinations.seaFreightServices.fclLcl')}</li>
                        <li>• {t('destinations.seaFreightServices.doorToDoor')}</li>
                        <li>• {t('destinations.seaFreightServices.importExport')}</li>
                        <li>• {t('destinations.seaFreightServices.hazardous')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* South Korea */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gradient-to-br from-blue-600 to-red-500">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-2">🇰🇷</div>
                      <h3 className="text-2xl font-bold">{t('countries.southKorea')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.majorPorts')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>• Busan</div>
                        <div>• Incheon</div>
                        <div>• Ulsan</div>
                        <div>• Pyeongtaek</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.transitTime')}</h4>
                      <p className="text-sm text-gray-600">{t('destinations.transitTimes.koreaToEurope')}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.services')}</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• {t('destinations.seaFreightServices.fclLcl')}</li>
                        <li>• {t('destinations.seaFreightServices.electronics')}</li>
                        <li>• {t('destinations.seaFreightServices.tempControlled')}</li>
                        <li>• {t('destinations.seaFreightServices.expressShipping')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* India */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gradient-to-br from-orange-500 to-green-600">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-2">🇮🇳</div>
                      <h3 className="text-2xl font-bold">{t('countries.india')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.majorPorts')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>• Mumbai</div>
                        <div>• Chennai</div>
                        <div>• Kolkata</div>
                        <div>• Cochin</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.transitTime')}</h4>
                      <p className="text-sm text-gray-600">{t('destinations.transitTimes.indiaToEurope')}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.services')}</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• {t('destinations.seaFreightServices.fclLcl')}</li>
                        <li>• {t('destinations.seaFreightServices.textiles')}</li>
                        <li>• {t('destinations.seaFreightServices.spices')}</li>
                        <li>• {t('destinations.seaFreightServices.projectCargo')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Canada */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gradient-to-br from-red-600 to-red-700">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-2">🇨🇦</div>
                      <h3 className="text-2xl font-bold">{t('countries.canada')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.majorPorts')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>• Vancouver</div>
                        <div>• Halifax</div>
                        <div>• Montreal</div>
                        <div>• Toronto</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.transitTime')}</h4>
                      <p className="text-sm text-gray-600">{t('destinations.transitTimes.canadaToEurope')}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.services')}</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• {t('destinations.seaFreightServices.fclLcl')}</li>
                        <li>• {t('destinations.seaFreightServices.naturalResources')}</li>
                        <li>• {t('destinations.seaFreightServices.machinery')}</li>
                        <li>• {t('destinations.seaFreightServices.frozenGoods')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Brazil */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gradient-to-br from-green-600 to-yellow-500">
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-2">🇧🇷</div>
                      <h3 className="text-2xl font-bold">{t('countries.brazil')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.majorPorts')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>• Santos</div>
                        <div>• Rio de Janeiro</div>
                        <div>• Salvador</div>
                        <div>• Paranaguá</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.transitTime')}</h4>
                      <p className="text-sm text-gray-600">{t('destinations.transitTimes.brazilToEurope')}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('destinations.services')}</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• {t('destinations.seaFreightServices.fclLcl')}</li>
                        <li>• {t('destinations.seaFreightServices.agricultural')}</li>
                        <li>• {t('destinations.seaFreightServices.coffee')}</li>
                        <li>• {t('destinations.seaFreightServices.bulkCargo')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Route Advantages */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('destinations.routeAdvantages.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('destinations.routeAdvantages.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: t('destinations.routeAdvantages.fastTransit.title'),
                description: t('destinations.routeAdvantages.fastTransit.description')
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
                title: t('destinations.routeAdvantages.reliablePartners.title'),
                description: t('destinations.routeAdvantages.reliablePartners.description')
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                ),
                title: t('destinations.routeAdvantages.competitiveRates.title'),
                description: t('destinations.routeAdvantages.competitiveRates.description')
              }
            ].map((advantage, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-orange-accent bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="text-orange-accent">
                    {advantage.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {advantage.title}
                </h3>
                <p className="text-gray-600">
                  {advantage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-orange-accent text-white">
        <div className="container-custom">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('destinations.ctaTitle')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('destinations.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const formElement = document.getElementById('request-form');
                  if (formElement) {
                    formElement.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    window.location.href = localizePath('/#request-form', i18n.language);
                  }
                }}
                className="btn-secondary text-lg px-8 py-4"
              >
                {t('common.getQuote')}
              </button>
              <a
                href="tel:+48577083279"
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium py-4 px-8 rounded-lg transition-all duration-300 text-lg"
              >
                {t('common.contactUs')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Destinations; 